import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.map.js";
import "core-js/modules/esnext.global-this.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { MessageChannel, ChannelType } from './channel';

/**
 * Global cache Symbol (used to store MessageChannel instance cache on window/globalThis).
 *
 * NOTE:
 * - We MUST use Symbol.for() so multiple library copies (different bundles/versions)
 *   can share the same cache.
 * - Keep the symbol key stable for backward compatibility with older releases.
 */
var MESSAGE_CHANNEL_CACHE_SYMBOL = Symbol.for('__requestIframeMessageChannelCache__');

/**
 * Get the cache host object.
 *
 * We prefer `window` because MessageChannel itself is window-based (postMessage listener),
 * and existing versions store the cache on window as well.
 */
function getCacheHost() {
  if (typeof window !== 'undefined') return window;
  // Fallback for non-browser environments; creating MessageChannel will likely fail anyway.
  return globalThis;
}

/**
 * Get the MessageChannel cache Map from the host.
 */
function getChannelCache() {
  var host = getCacheHost();
  if (!(MESSAGE_CHANNEL_CACHE_SYMBOL in host)) {
    host[MESSAGE_CHANNEL_CACHE_SYMBOL] = new Map();
  }
  return host[MESSAGE_CHANNEL_CACHE_SYMBOL];
}

/**
 * Generate cache key.
 * Format: "type:secretKey" or "type:" when no secretKey.
 */
function getCacheKey(type, secretKey) {
  return `${type}:${secretKey !== null && secretKey !== void 0 ? secretKey : ''}`;
}

/**
 * Get or create MessageChannel instance.
 *
 * - Within the same window, only one channel is created per type + secretKey.
 * - Uses reference counting to manage lifecycle.
 */
export function getOrCreateMessageChannel(secretKey, type = ChannelType.POST_MESSAGE) {
  var cache = getChannelCache();
  var key = getCacheKey(type, secretKey);
  var channel = cache.get(key);
  if (!channel) {
    channel = new MessageChannel(secretKey, type);
    cache.set(key, channel);
  }
  channel.addRef();
  return channel;
}

/**
 * Release MessageChannel reference.
 *
 * - When reference count reaches 0, destroy channel and remove from cache.
 */
export function releaseMessageChannel(channel) {
  var refCount = channel.release();
  if (refCount > 0) return;
  var cache = getChannelCache();
  var key = getCacheKey(channel.type, channel.secretKey);
  if (cache.get(key) === channel) {
    cache.delete(key);
    channel.destroy();
  }
}

/**
 * Clear MessageChannel cache (mainly for testing).
 *
 * - No args: clear all channels
 * - string arg: treated as secretKey filter
 * - params arg: filter by secretKey/type
 */
export function clearMessageChannelCache(arg) {
  var cache = getChannelCache();
  if (!arg) {
    cache.forEach(channel => {
      channel.destroy();
    });
    cache.clear();
    return;
  }
  var params = typeof arg === 'string' ? {
    secretKey: arg
  } : arg;
  cache.forEach((channel, key) => {
    if (params.type && channel.type !== params.type) return;
    if (typeof params.secretKey === 'string' && channel.secretKey !== params.secretKey) return;
    cache.delete(key);
    channel.destroy();
  });
}